/**
 * The body of an assistant turn: prose, then everything the prose refers to.
 *
 * The order is deliberate and matches how a person reads an answer — what to
 * do, then what it was based on, then any forms to fill in, then the tickets
 * involved.
 *
 * The same component renders both a live turn and a stored one. During
 * streaming no "ungrounded" note appears — a half-finished answer has not
 * earned that judgement.
 */

import { Check, Copy, Icon, RefreshCw } from "@norican/icons";
import { Alert, Button, Typography } from "@norican/react";
import { useState } from "react";

import { ErrorState, Markdown } from "@/components/common";
import { FeedbackControls } from "@/features/feedback";
import { useT } from "@/i18n";
import type { MyFeedback } from "@/lib/api";
import { logger } from "@/lib/logger";

import { AccessCardList } from "./access-card";
import { useAssistantCapability } from "./assistant-capability";
import { showsFallbackLinks } from "./assistant-fallback";
import { AssistantFallbackLinks } from "./assistant-fallback-links";
import { CitationList, UngroundedNote } from "./citation-list";
import type {
  ApiErrorResponse,
  AssistantAccessCardEvent,
  AssistantCitation,
  AssistantIncidentFormEvent,
  AssistantMessageStatus,
  AssistantRequestFormEvent,
  AssistantStatusEvent,
  TicketRef,
  TranslatableText,
} from "./contract";
import { FormErrorBoundary } from "./form-error-boundary";
import { IncidentFormList } from "./incident-form-card";
import type { ChatFile } from "./lib/chat-files";
import { TranslatedText } from "./localized";
import { RequestFormList } from "./request-form-card";
import { StreamStatus } from "./stream-status";
import { TicketRefList } from "./ticket-ref-card";

/** Copy-confirmation dwell before the tick reverts to the copy icon. */
const COPIED_RESET_MS = 2000;

function CopyAnswerButton({ text }: { text: string }) {
  const { t } = useT("assistant");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch((error: unknown) => {
        // Clipboard access is denied in some managed browser profiles; this is
        // a convenience, so it fails quietly rather than alarming the user.
        logger.warn("[assistant] clipboard write refused", error);
      });
  };

  return (
    <Button
      variant="ghost"
      tone="neutral"
      size="sm"
      iconOnly
      aria-label={t("message.copy")}
      onClick={handleCopy}
    >
      <Icon icon={copied ? Check : Copy} size="sm" tone={copied ? "success" : undefined} />
    </Button>
  );
}

export interface AssistantAnswerProps {
  /** Stored message content, when there is one. Carries the translation affordance. */
  content?: TranslatableText | null;
  /** Prose accumulated from the stream, used while `content` does not exist yet. */
  streamingText?: string;
  status: AssistantMessageStatus;
  citations: AssistantCitation[];
  /**
   * Whether the turn searched Norican's knowledge, from the server.
   *
   * Not inferable here: an empty `citations` list cannot tell "looked and
   * Norican has nothing written down" from "never looked", and only the first
   * of those is worth telling the reader about.
   */
  knowledgeConsulted?: boolean;
  ticketRefs: TicketRef[];
  /**
   * Catalog forms the assistant streamed for this turn.
   *
   * These do not come back from the server on a reload — the persisted message
   * has nowhere to carry them — so the page that owns the transcript keeps them
   * and hands them back here.
   */
  requestForms?: AssistantRequestFormEvent[];
  /**
   * Prefilled incident reports the assistant streamed for this turn.
   *
   * Stream-only in the same way `requestForms` is, and kept by the same owner.
   */
  incidentForms?: AssistantIncidentFormEvent[];
  /** "Start an access request" cards (6 Oct). Stream-only, kept like the forms. */
  accessCards?: AssistantAccessCardEvent[];
  /** Stable prefix for each form's "already filed" key. Usually the message id. */
  requestFormScope?: string | null;
  /**
   * Draft attachment ids the user's message carried.
   *
   * Threaded onto anything raised from this turn, so the screenshot that came
   * with the question lands on the ticket rather than staying in the chat.
   */
  attachmentIds?: string[];
  /**
   * Files attached in the chat up to this turn, with their names.
   *
   * Carried onto a request filed from a form card in this turn — the incident
   * card has always taken the turn's files; the request card did not
   * (LIVE #10578, 8 Oct).
   */
  chatFiles?: ChatFile[];
  /** Threaded onto a filed request so it lands against the right conversation. */
  conversationId?: string | null;
  /**
   * The persisted assistant message id — the 👍/👎 target. Without it (or
   * before the turn completes) no feedback control is shown: a half-finished
   * answer has not earned a verdict.
   */
  messageId?: string | null;
  /** The reader's own verdict as the server last reported it. */
  myFeedback?: MyFeedback | null;
  /** The live activity line. Only rendered while the turn is unfinished. */
  activity?: AssistantStatusEvent | null;
  error?: ApiErrorResponse | null;
  /** Transport-level failure, as opposed to a modelled `error` event. */
  transportFailed?: boolean;
  /**
   * Announce "the answer is ready" when this turn completes.
   *
   * Only the turn that is actually streaming sets this. Rendering the cue on
   * every stored message would put one live region per message into the DOM on
   * page load, and some screen readers read those out on insertion.
   */
  announceCompletion?: boolean;
  onRetry?: () => void;
}

export function AssistantAnswer({
  content = null,
  streamingText = "",
  status,
  citations,
  knowledgeConsulted = false,
  ticketRefs,
  requestForms = [],
  incidentForms = [],
  accessCards = [],
  requestFormScope = null,
  attachmentIds = [],
  chatFiles = [],
  conversationId = null,
  messageId = null,
  myFeedback = null,
  activity = null,
  error = null,
  transportFailed = false,
  announceCompletion = false,
  onRetry,
}: AssistantAnswerProps) {
  const { t } = useT("assistant");
  const { available } = useAssistantCapability();

  const text = content?.text ?? streamingText;
  const inProgress = status === "PENDING" || status === "STREAMING";
  const isComplete = status === "COMPLETE";

  return (
    <>
      {text === "" ? null : content !== null && content.translated ? (
        <TranslatedText value={content} render={(shown) => <Markdown>{shown}</Markdown>} />
      ) : (
        <Markdown>{text}</Markdown>
      )}

      {/* A caret while prose is still arriving: cheaper to read than a spinner
          that sits beside finished text. */}
      {inProgress && text !== "" ? (
        <span
          className="bg-accent inline-block h-4 w-[2px] animate-pulse align-text-bottom"
          aria-hidden="true"
        />
      ) : null}

      {/* One live region per turn. Rendering a second one for the
          "nothing yet" case would announce the same phase twice as soon as the
          first token arrived. */}
      {inProgress ? <StreamStatus activity={activity} /> : null}

      {/* Sources, or an explicit note that there were none — but only when the
          turn actually went looking. It used to appear under every completed
          answer with no citations, which meant it appeared under clarifying
          questions and under turns whose whole job was to open a form. Six of
          them in one reported conversation, none attached to a claim. A
          disclaimer on something that asserts nothing is noise, and noise is
          how a disclaimer stops being read where it matters. */}
      {citations.length > 0 ? (
        <CitationList citations={citations} />
      ) : knowledgeConsulted && isComplete && text !== "" ? (
        <UngroundedNote />
      ) : null}

      {requestFormScope === null ? null : (
        <FormErrorBoundary>
          <AccessCardList cards={accessCards} />
          <RequestFormList
            forms={requestForms}
            scopeId={requestFormScope}
            conversationId={conversationId}
            chatFiles={chatFiles}
          />
          <IncidentFormList
            forms={incidentForms}
            scopeId={requestFormScope}
            conversationId={conversationId}
            attachmentIds={attachmentIds}
          />
        </FormErrorBoundary>
      )}

      <TicketRefList tickets={ticketRefs} />

      {status === "CANCELLED" ? <Alert tone="neutral">{t("message.cancelled")}</Alert> : null}

      {/* A turn that failed because the assistant is unavailable carries the
          plain-form links, so the person is never left with only "try again".
          Any other failure is retried in the chat. */}
      {error !== null ? (
        <ErrorState
          variant="inline"
          error={{ status: null, body: error }}
          onRetry={onRetry}
          actions={
            showsFallbackLinks({ available, errorCode: error.code }) ? (
              <AssistantFallbackLinks />
            ) : undefined
          }
        />
      ) : null}

      {error === null && (status === "FAILED" || transportFailed) ? (
        <ErrorState
          variant="inline"
          title={t("message.failed")}
          description={t("message.failedHint")}
          onRetry={onRetry}
          actions={showsFallbackLinks({ available }) ? <AssistantFallbackLinks /> : undefined}
        />
      ) : null}

      {isComplete && text !== "" ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          {messageId === null ? (
            <span />
          ) : (
            <FeedbackControls messageId={messageId} initial={myFeedback} />
          )}
          <div className="flex items-center gap-1">
            <CopyAnswerButton text={text} />
            {onRetry === undefined ? null : (
              <Button
                variant="ghost"
                tone="neutral"
                size="sm"
                startIcon={<Icon icon={RefreshCw} size="sm" />}
                onClick={onRetry}
              >
                {t("message.retry")}
              </Button>
            )}
          </div>
        </div>
      ) : null}

      {/* Screen-reader-only completion cue. The transcript itself is not a live
          region — announcing every token would make the page unusable — so this
          single polite message is how a non-sighted user learns the answer has
          finished arriving.

          The region is always mounted and only its text changes: a live region
          inserted into the DOM with its content already in place is not
          reliably announced, because assistive technology has to observe the
          region before it can notice a change inside it. Only the streaming
          turn owns one, so a long transcript does not carry a dozen. */}
      {announceCompletion ? (
        <Typography variant="caption" className="sr-only" role="status" aria-live="polite">
          {isComplete ? t("a11y.answerReady") : ""}
        </Typography>
      ) : null}
    </>
  );
}
